import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Cpu,
  Bot,
  BarChart3,
  Zap,
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  Shield,
  Clock,
  Globe,
  Layers
} from "lucide-react";
import { Container, Section, PrimaryButton, Badge } from "../styles/GlobalStyles";

/* ── Animations ─────────────────────────────── */
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.05); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

/* ── Layout ──────────────────────────────────── */
const PageWrapper = styled.div`
  background: #0a0f1e;
  min-height: 100vh;
  overflow-x: hidden;
`;

/* ── Hero ────────────────────────────────────── */
const HeroSection = styled.section`
  position: relative;
  min-height: 90vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: radial-gradient(ellipse at 60% 0%, rgba(96,165,250,0.18) 0%, transparent 60%),
              radial-gradient(ellipse at 0% 80%, rgba(168,85,247,0.15) 0%, transparent 60%),
              #0a0f1e;
  padding: 100px 0 60px;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 80px 0 50px;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`;

const HeroLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  background: rgba(96,165,250,0.12);
  border: 1px solid rgba(96,165,250,0.3);
  color: #60a5fa;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
`;

const HeroHeading = styled.h1`
  font-size: clamp(2.4rem, 5vw, 4rem);
  font-weight: 900;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;

  .highlight {
    background: linear-gradient(90deg, #60a5fa, #a78bfa, #f472b6);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const HeroSub = styled.p`
  font-size: 1.1rem;
  color: #94a3b8;
  line-height: 1.7;
  max-width: 500px;
  margin-bottom: 2.5rem;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const OutlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 2rem;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.05);
  color: #e2e8f0;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);

  &:hover {
    border-color: rgba(96,165,250,0.5);
    background: rgba(96,165,250,0.1);
    color: #60a5fa;
    transform: translateY(-2px);
  }
`;

/* Floating visual widget */
const HeroVisual = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const FloatingOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  animation: ${pulse} 6s ease-in-out infinite;
`;

const AiCard = styled(motion.div)`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  padding: 1.5rem;
  backdrop-filter: blur(16px);
  min-width: 260px;

  .title {
    font-size: 0.75rem;
    font-weight: 700;
    color: #60a5fa;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 0.75rem;
  }

  .metric {
    font-size: 2.5rem;
    font-weight: 900;
    color: #fff;
    line-height: 1;
    margin-bottom: 0.25rem;
  }

  .label {
    font-size: 0.85rem;
    color: #94a3b8;
  }

  .bar-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  .bar-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: #94a3b8;
  }

  .bar-track {
    flex: 1;
    height: 6px;
    background: rgba(255,255,255,0.08);
    border-radius: 999px;
    overflow: hidden;
  }

  .bar-fill {
    height: 100%;
    border-radius: 999px;
    background: linear-gradient(90deg, #60a5fa, #a78bfa);
  }
`;

/* ── Solutions ───────────────────────────────── */
const SolutionsSection = styled.section`
  padding: 7rem 0;
  background: #0d1425;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const SectionLabel = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  background: rgba(167,139,250,0.1);
  border: 1px solid rgba(167,139,250,0.25);
  color: #a78bfa;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;

  span {
    background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SectionSub = styled(motion.p)`
  color: #64748b;
  font-size: 1.05rem;
  line-height: 1.7;
  max-width: 600px;
  margin-bottom: 3.5rem;
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SolutionCard = styled(motion.div)`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 20px;
  padding: 2rem;
  transition: all 0.35s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, ${p => p.glow || 'rgba(96,165,250,0.05)'} 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.35s ease;
  }

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(96,165,250,0.2);
    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.4);

    &::before { opacity: 1; }
  }

  .icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: ${p => p.iconBg || 'rgba(96,165,250,0.12)'};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.25rem;
    color: ${p => p.iconColor || '#60a5fa'};
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 0.6rem;
  }

  p {
    font-size: 0.9rem;
    color: #64748b;
    line-height: 1.6;
  }
`;

/* ── Stats ───────────────────────────────────── */
const StatsSection = styled.section`
  padding: 5rem 0;
  background: linear-gradient(135deg, #1e1b4b 0%, #0f172a 50%, #0a1628 100%);
  border-top: 1px solid rgba(255,255,255,0.06);
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  text-align: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.25rem;
  }
`;

const StatItem = styled(motion.div)`
  .number {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 900;
    background: linear-gradient(135deg, #60a5fa, #a78bfa);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1;
    margin-bottom: 0.5rem;
  }

  .desc {
    font-size: 0.9rem;
    color: #64748b;
    font-weight: 500;
  }
`;

/* ── Benefits ────────────────────────────────── */
const BenefitsSection = styled.section`
  padding: 7rem 0;
  background: #0a0f1e;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const BenefitItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(96,165,250,0.2);
    background: rgba(96,165,250,0.05);
    transform: translateX(4px);
  }

  .check {
    flex-shrink: 0;
    margin-top: 2px;
  }

  h4 {
    font-size: 0.95rem;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 0.2rem;
  }

  p {
    font-size: 0.85rem;
    color: #64748b;
    line-height: 1.5;
  }
`;

const TechStack = styled(motion.div)`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 24px;
  padding: 2.5rem;

  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: #f1f5f9;
    margin-bottom: 1.5rem;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;

    span {
      padding: 0.4rem 0.9rem;
      border-radius: 8px;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.1);
      color: #94a3b8;
      font-size: 0.82rem;
      font-weight: 500;
      transition: all 0.2s;
      cursor: default;

      &:hover {
        background: rgba(96,165,250,0.12);
        border-color: rgba(96,165,250,0.3);
        color: #60a5fa;
      }
    }
  }

  .process {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .step {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.88rem;
    color: #94a3b8;

    .num {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: rgba(96,165,250,0.15);
      border: 1px solid rgba(96,165,250,0.3);
      color: #60a5fa;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.7rem;
      font-weight: 700;
      flex-shrink: 0;
    }
  }
`;

/* ── CTA ─────────────────────────────────────── */
const CTASection = styled.section`
  padding: 7rem 0;
  background: radial-gradient(ellipse at 50% 0%, rgba(96,165,250,0.15) 0%, transparent 60%),
              #0d1425;
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const CTAHeading = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 900;
  color: #fff;
  margin-bottom: 1.25rem;
  letter-spacing: -0.02em;

  span {
    background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 60%, #f472b6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const CTASub = styled(motion.p)`
  color: #64748b;
  font-size: 1.1rem;
  max-width: 560px;
  margin: 0 auto 2.5rem;
  line-height: 1.7;
`;

/* ─────────────────────────────────────────────── */

const solutions = [
  {
    icon: <Brain size={24} />,
    title: "Machine Learning",
    desc: "Custom ML models trained on your data — for predictions, anomaly detection, and intelligent automation.",
    iconBg: "rgba(96,165,250,0.12)",
    iconColor: "#60a5fa",
    glow: "rgba(96,165,250,0.06)",
  },
  {
    icon: <Cpu size={24} />,
    title: "Process Automation",
    desc: "End-to-end workflow automation that eliminates repetitive tasks and reduces operational costs.",
    iconBg: "rgba(167,139,250,0.12)",
    iconColor: "#a78bfa",
    glow: "rgba(167,139,250,0.06)",
  },
  {
    icon: <Bot size={24} />,
    title: "AI Chatbots & Agents",
    desc: "24/7 NLP-powered assistants that handle support, sales, and complex multi-step reasoning.",
    iconBg: "rgba(244,114,182,0.12)",
    iconColor: "#f472b6",
    glow: "rgba(244,114,182,0.06)",
  },
  {
    icon: <BarChart3 size={24} />,
    title: "Predictive Analytics",
    desc: "Forecast demand, churn, and revenue with enterprise-grade data pipelines and BI dashboards.",
    iconBg: "rgba(52,211,153,0.12)",
    iconColor: "#34d399",
    glow: "rgba(52,211,153,0.06)",
  },
];

const stats = [
  { number: "40–60%", desc: "Cost reduction in operations" },
  { number: "90%+", desc: "Accuracy in model predictions" },
  { number: "24/7", desc: "Automated customer support" },
  { number: "3×", desc: "Faster time-to-insight" },
];

const benefits = [
  {
    title: "Real-Time Data Processing",
    desc: "Ingest and analyse millions of events per second with scalable stream-processing pipelines.",
  },
  {
    title: "Scalable AI Infrastructure",
    desc: "Cloud-agnostic deployments on AWS, Azure, or GCP — auto-scaling to any workload.",
  },
  {
    title: "Enterprise-Grade Security",
    desc: "SOC-2 aligned practices, encrypted model artefacts, and role-based access control.",
  },
  {
    title: "Seamless Integration",
    desc: "REST & GraphQL APIs that plug into your existing ERP, CRM, or custom stack in days.",
  },
  {
    title: "Continuous Learning",
    desc: "Models retrain automatically on new data — staying accurate without manual intervention.",
  },
];

const techTags = [
  "Python", "TensorFlow", "PyTorch", "Scikit-learn", "LangChain",
  "OpenAI GPT", "FastAPI", "React", "Node.js", "PostgreSQL",
  "Redis", "Docker", "Kubernetes", "AWS", "Azure",
];

const processSteps = [
  "Discovery & data audit",
  "Model design & prototyping",
  "Training & evaluation",
  "Deployment & monitoring",
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" }
  }),
};

/* ─────────────────────────────────────────────── */

const Discover = () => {
  const navigate = useNavigate();

  return (
    <PageWrapper>
      {/* ── HERO ── */}
      <HeroSection>
        <Container>
          <HeroGrid>
            <div>
              <HeroLabel><Zap size={12} /> AI & Automation</HeroLabel>
              <HeroHeading>
                Intelligent Systems That <span className="highlight">Learn & Scale</span> With Your Business
              </HeroHeading>
              <HeroSub>
                We architect AI-powered platforms — from custom ML models and RAG pipelines
                to end-to-end automation — built for production and real-world scale.
              </HeroSub>
              <HeroActions>
                <PrimaryButton onClick={() => navigate("/contact")}>
                  Start Your AI Project <ArrowRight size={16} />
                </PrimaryButton>
                <OutlineButton onClick={() => navigate("/products")}>
                  View Our Products
                </OutlineButton>
              </HeroActions>
            </div>

            <HeroVisual>
              <FloatingOrb style={{ width: 300, height: 300, background: "rgba(96,165,250,0.15)", top: "5%", left: "10%" }} />
              <FloatingOrb style={{ width: 200, height: 200, background: "rgba(167,139,250,0.1)", bottom: "10%", right: "5%", animationDelay: "3s" }} />
              <AiCard
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="title">Model Performance</div>
                <div className="metric">94.7%</div>
                <div className="label">Prediction accuracy</div>
                <div className="bar-row">
                  {[{ label: "Precision", w: "92%" }, { label: "Recall", w: "88%" }, { label: "F1 Score", w: "94%" }].map(b => (
                    <div className="bar-item" key={b.label}>
                      <span style={{ width: 56, flexShrink: 0 }}>{b.label}</span>
                      <div className="bar-track"><div className="bar-fill" style={{ width: b.w }} /></div>
                      <span>{b.w}</span>
                    </div>
                  ))}
                </div>
              </AiCard>
            </HeroVisual>
          </HeroGrid>
        </Container>
      </HeroSection>

      {/* ── SOLUTIONS ── */}
      <SolutionsSection>
        <Container>
          <SectionLabel
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Layers size={12} /> What We Build
          </SectionLabel>
          <SectionTitle
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            AI Solutions <span>We Engineer</span>
          </SectionTitle>
          <SectionSub
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Custom-built artificial intelligence tailored to your specific business goals,
            industry, and data landscape.
          </SectionSub>

          <SolutionsGrid>
            {solutions.map((s, i) => (
              <SolutionCard
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                glow={s.glow}
                iconBg={s.iconBg}
                iconColor={s.iconColor}
              >
                <div className="icon-wrap">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </SolutionCard>
            ))}
          </SolutionsGrid>
        </Container>
      </SolutionsSection>

      {/* ── STATS ── */}
      <StatsSection>
        <Container>
          <StatsGrid>
            {stats.map((s, i) => (
              <StatItem
                key={i}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="number">{s.number}</div>
                <div className="desc">{s.desc}</div>
              </StatItem>
            ))}
          </StatsGrid>
        </Container>
      </StatsSection>

      {/* ── BENEFITS ── */}
      <BenefitsSection>
        <Container>
          <BenefitsGrid>
            <div>
              <SectionLabel
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <TrendingUp size={12} /> Why Choose Us
              </SectionLabel>
              <SectionTitle
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                Built for <span>Performance</span>
              </SectionTitle>
              <SectionSub
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                Every AI system we deliver is engineered for reliability, security, and long-term value.
              </SectionSub>

              <BenefitsList>
                {benefits.map((b, i) => (
                  <BenefitItem
                    key={i}
                    custom={i}
                    variants={cardVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                  >
                    <CheckCircle2 size={18} color="#34d399" className="check" />
                    <div>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </div>
                  </BenefitItem>
                ))}
              </BenefitsList>
            </div>

            <TechStack
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3>Tech Stack & Tools</h3>
              <div className="tags">
                {techTags.map(t => <span key={t}>{t}</span>)}
              </div>
              <div className="process">
                {processSteps.map((step, i) => (
                  <div className="step" key={i}>
                    <div className="num">{i + 1}</div>
                    {step}
                  </div>
                ))}
              </div>
            </TechStack>
          </BenefitsGrid>
        </Container>
      </BenefitsSection>

      {/* ── CTA ── */}
      <CTASection>
        <Container>
          <CTAHeading
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Automate <span>Your Future?</span>
          </CTAHeading>
          <CTASub
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Our AI specialists will analyse your business and build the exact solution
            you need — from proof of concept to production.
          </CTASub>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            style={{ display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}
          >
            <PrimaryButton onClick={() => navigate("/contact")}>
              Get a Free AI Audit <ArrowRight size={16} />
            </PrimaryButton>
            <OutlineButton onClick={() => navigate("/products")}>
              <Globe size={16} /> Explore Our Products
            </OutlineButton>
          </motion.div>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

export default Discover;
